import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

export const SetupWarnings = () => {
  const [w, setW] = useState([]);
  useEffect(() => { api.get("/admin/setup").then((r) => setW(r.data.warnings)); }, []);
  if (!w.length) return null;
  return (
    <div className="border border-[hsl(30_50%_75%)] bg-[hsl(35_70%_94%)] rounded-lg p-4 mb-4 text-sm" data-testid="setup-warnings">
      <p className="font-semibold mb-1">Setup required before launch</p>
      <ul className="list-disc pl-5 space-y-0.5">{w.map((x, i) => <li key={i} data-testid={`setup-warning-${i}`}>{x}</li>)}</ul>
    </div>
  );
};

export const BookingStatusControl = ({ settings, reload }) => {
  const bs = settings.booking_status;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState(bs.message);
  const [busy, setBusy] = useState(false);
  const next = !bs.active;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/booking-status", { active: next, reason, message });
      toast.success(next ? "Bookings reactivated. The site is accepting reservations again." : `Bookings paused.${data.unpaid_holds_released ? ` ${data.unpaid_holds_released} unpaid checkout(s) released.` : ""}`);
      setOpen(false); setReason(""); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-lg border p-4 sm:p-5 mb-4 flex flex-wrap items-center justify-between gap-4 ${bs.active ? "bg-white border-border" : "bg-[hsl(4_60%_96%)] border-[hsl(4_50%_75%)]"}`} data-testid="booking-status-bar">
      <div>
        <p className="flex items-center gap-2 font-semibold" data-testid="booking-status-indicator">
          <span className={`w-2.5 h-2.5 rounded-full ${bs.active ? "bg-[hsl(var(--sage))]" : "bg-destructive"}`} />
          {bs.active ? "Bookings ACTIVE: accepting new reservations" : "Bookings DEACTIVATED: new reservations are blocked"}
        </p>
        {bs.updated_at && <p className="text-xs text-muted-foreground mt-1">Last changed {new Date(bs.updated_at).toLocaleString()} by {bs.updated_by}{bs.reason && ` · ${bs.reason}`}</p>}
      </div>
      <button onClick={() => setOpen(true)} className={`px-5 py-2.5 rounded-full text-sm font-semibold ${bs.active ? "bg-destructive text-white" : "bg-[hsl(var(--sage))] text-white"}`} data-testid="booking-status-toggle-button">
        {bs.active ? "Overbooked / Deactivate Bookings" : "Reactivate Bookings"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="booking-status-dialog">
          <DialogTitle>{next ? "Reactivate bookings?" : "Deactivate new bookings?"}</DialogTitle>
          <DialogDescription>{next ? "Guests will be able to book again. The 14-night minimum, blocked dates and existing reservations still apply." : "New reservations will be rejected and unpaid checkouts in progress will be released. Existing reservations, payments and records are kept."}</DialogDescription>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label htmlFor="bs-reason">Reason (for the activity log)</Label><Input id="bs-reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="booking-status-reason-input" /></div>
            {!next && <div className="space-y-1.5"><Label htmlFor="bs-msg">Message shown to guests</Label><Textarea id="bs-msg" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="booking-status-message-input" /></div>}
          </div>
          <div className="flex justify-end gap-3 mt-2">
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm" data-testid="booking-status-cancel">Cancel</button>
            <button onClick={save} disabled={busy} className="bg-primary text-primary-foreground px-5 py-2 rounded-full text-sm disabled:opacity-60" data-testid="booking-status-confirm">{busy ? "Saving…" : next ? "Yes, reactivate" : "Yes, deactivate"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
